import './DataDictionarySearch.scss';

import { AppDisplay } from '@ui/applications';
import { ComplianceCategories } from '@ui/icons';
import { createAsyncSelectors } from '@lib/selectors';
import { DefaultOptionType } from 'antd/es/select/index';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { QuickAccessViewButton } from './QuickAccessViewButton';
import { SearchOutlined } from '@ant-design/icons';
import { SearchTypeLabel } from './SearchTypeLabel';
import { ShowMoreTypeLabel } from './ShowMoreTypeLabel';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { SonarTagDisplay } from '@ui/tags';
import { SonarText } from '@ui/text';

import { Button, Select, Space } from 'antd';
import {
	CATEGORY_SEARCH_LIMIT,
	COPY_SPECIFIC,
	PLACEHOLDERS_COPY,
	QUICK_SEARCH_LIMIT
} from '@sonar-software/sonar-ts-constants';
import { ComplianceCategory, coreActions, GetSonarObjectsSearchResponse } from '@sonar-software/sonar-core';
import {
	getSonarObjectTypeFromId,
	getSonarObjectTypeId,
	getSonarObjectTypeLabelPlural
} from '@sonar-software/sonar-ts-lib';
import React, { Key, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
	UpdateQueryParams,
	useAction,
	useCustomViews,
	useDataDictionaryReset,
	useDataSecurityApps,
	useLatestImportCounts,
	useOnUnmount,
	usePeriscopeReset,
	useSonarSelector,
	useTags
} from '@hooks';

export enum SearchOptionGroup {
	Application = 'application',
	Compliance = 'complianceCategory',
	Object = 'object',
	ObjectType = 'objectType',
	Tag = 'tag',
	View = 'view'
}

export interface SearchOption extends DefaultOptionType {
	group?: SearchOptionGroup;
	key?: Key;
	rawValue?: string | number | null;
	filterString?: string | number | null;
}

export type SearchGroup = SearchOption & {
	children: Array<SearchOption>;
	key: Key
};

export const sonarObjectTypeOrder = [
	SonarObjectType.SalesforceEntity,
	SonarObjectType.SalesforceField,
	SonarObjectType.SalesforceWorkflowRule,
	SonarObjectType.SalesforceFieldUpdate,
	SonarObjectType.SalesforceWorkflowAlert,
	SonarObjectType.SalesforceValidationRule,
	SonarObjectType.SalesforceProcessBuilder,
	SonarObjectType.SalesforceFlow,
	SonarObjectType.SalesforceApexClass,
	SonarObjectType.SalesforceApexTrigger,
	SonarObjectType.SalesforceProfile,
	SonarObjectType.SalesforcePageLayout,
	SonarObjectType.SalesforceEmailTemplate
];

const getUniqueOption = (option: SearchOption): SearchOption => ({
	...option,
	filterString: option.filterString ?? String(option.label),
	key: option.key ?? `${option.group}-${option.value}`,
	rawValue: option.value,
	value: `${option.group}-${option.value}`
});

const typeOptions: Array<SearchOption> = Object.values(sonarObjectTypeOrder).map(
	(type) =>
		getUniqueOption({
			group: SearchOptionGroup.ObjectType,
			label: getSonarObjectTypeLabelPlural(type),
			value: type
		})
);

const complianceCategories = Object.entries(ComplianceCategory).filter(([ , id ]) => typeof id === 'number');

function getSortedDataKeys(data: GetSonarObjectsSearchResponse | null | undefined) {
	if (!data) {
		return [];
	}

	const keys = Object.keys(data).filter(
		(item: SonarObjectType) => [ sonarObjectTypeOrder[ 0 ] ].indexOf(item) > -1
	) as SonarObjectType[];

	return keys.sort((a, b) => [ sonarObjectTypeOrder[ 0 ] ].indexOf(a) - [ sonarObjectTypeOrder[ 0 ] ].indexOf(b));
}

const buildOptionsGroup = <T = unknown>(
	group: SearchOptionGroup,
	groupLabel: ReactNode,
	showMore: (group: SearchOptionGroup) => {show: boolean, toggle: () => void},
	allOptions: T[] = [],
	mapOption: (option: T) => SearchOption
): DefaultOptionType | SearchOption[] => {
	const { show, toggle } = showMore(group);

	const options = (show ? allOptions : allOptions.slice(0, 10))
		.map((option) => getUniqueOption({
			...mapOption(option),
			group
		}));

	const label = typeof groupLabel === 'string' ?
		<ShowMoreTypeLabel
			allowShowMore={ !show && allOptions.length > 10 }
			label={ groupLabel }
			onClick={ toggle } /> : groupLabel;

	if (groupLabel) {
		return {
			label,
			options
		};
	}

	return options;
};

export interface DataDictionarySearchQueryParams {
	applicationId?: number;
	complianceCategory?: ComplianceCategory;
	displayName?: string;
	sonarObjectId?: number;
	sonarObjectType?: SonarObjectType;
	tagId?: number;
	viewId?: number;
	viewName?: string;
}

export interface DataDictionarySearchProps {
	queryParams: DataDictionarySearchQueryParams;
	updateQuery: UpdateQueryParams<DataDictionarySearchQueryParams>;
}

const renderOptions = ({ data: option }: {data: Partial<SearchOption>}) => {
	switch (option.group) {
	case SearchOptionGroup.Application:
		return <AppDisplay appId={ Number(option.rawValue) } />;
	case SearchOptionGroup.Tag:
		return <SonarTagDisplay
			noClick
			tagId={ Number(option.rawValue) } />;
	case SearchOptionGroup.View:
		return <QuickAccessViewButton viewId={ Number(option.rawValue) } />;
	case SearchOptionGroup.ObjectType:
		return <SearchTypeLabel sonarObjectType={ option.rawValue as SonarObjectType } />;
	case SearchOptionGroup.Compliance:
		return <ComplianceCategories categoryFlags={ Number(option.rawValue) } />;
	default:
		return option.label ?? '';
	}
};

const searchSelectors = createAsyncSelectors((state) => state.core.dataDictionary.search.get);

export const DataDictionarySearch: React.FC<DataDictionarySearchProps> = ({ queryParams, updateQuery }) => {
	const [ query, setQuery ] = useState(queryParams.displayName ?? '');
	const [ objectTypeFilter, setObjectTypeFilter ] = useState<string>();
	const [ shouldShowMore, setShouldShowMore ] =
		useState<Partial<Record<SearchOptionGroup, boolean>>>({});

	const showMore = useCallback((group: SearchOptionGroup) => ({
		show: Boolean(shouldShowMore[ group ]),
		toggle: () => setShouldShowMore(
			(prevState) => ({
				...prevState,
				[ group ]: !prevState[ group ]
			})
		)
	}), [ shouldShowMore ]);

	const isLoading = useSonarSelector(searchSelectors.isRequested);
	const results = useSonarSelector(searchSelectors.data);
	const { relatedCounts } = useLatestImportCounts(true);

	const views = useCustomViews();
	const tags = useTags();
	const apps = useDataSecurityApps();

	const getSearch = useAction(coreActions.requestGetCoreDataDictionarySearch);

	const resetPeriscopes = usePeriscopeReset();
	const { resetData, resetSearch } = useDataDictionaryReset();

	const handleSearchReset = () => {
		resetSearch();
		setQuery('');
		setObjectTypeFilter(undefined);
		setShouldShowMore({});
	};

	const cleanupState = () => {
		handleSearchReset();
		resetData();
	};

	const handleOnClear = () => {
		cleanupState();
		resetPeriscopes();
		updateQuery({});
	};

	useOnUnmount(cleanupState);

	useEffect(() => {
		if (query?.length >= 3) {
			getSearch({
				maxResultsPerType: objectTypeFilter ? CATEGORY_SEARCH_LIMIT : QUICK_SEARCH_LIMIT,
				objectTypeFilters: objectTypeFilter ?
					[ getSonarObjectTypeId(objectTypeFilter as SonarObjectType) ] :
					undefined,
				queryString: query
			});
		}
	}, [ objectTypeFilter, query, getSearch ]);

	const handleOnSearch = (value: string) => {
		const cleanValue = value?.trim();

		setQuery(cleanValue);

		if (!cleanValue?.length || (!objectTypeFilter && results !== null)) {
			resetSearch();
		}
	};

	const handleOnSelect = (_option: DefaultOptionType, item: SearchOption) => {
		resetPeriscopes();

		const { label, group, rawValue } = item;

		if (group === SearchOptionGroup.View) {
			const viewParams = views?.find((view) => view.id === Number(rawValue));
			const viewDisplayName = viewParams?.sonarObjectName ??
				apps?.find((app) => app.applicationId === viewParams?.applicationId)?.name;
			const sonarObjectType = viewParams?.sonarObjectTypeId ?
				getSonarObjectTypeFromId(viewParams?.sonarObjectTypeId) : undefined;

			updateQuery({
				applicationId: viewParams?.applicationId,
				complianceCategory: viewParams?.complianceCategory,
				displayName: viewDisplayName,
				sonarObjectId: viewParams?.sonarObjectId,
				sonarObjectType,
				tagId: viewParams?.tagId,
				viewId: viewParams?.id,
				viewName: viewParams?.name
			});
		} else {
			updateQuery({
				applicationId: group === SearchOptionGroup.Application ? Number(rawValue) : undefined,
				complianceCategory: group === SearchOptionGroup.Compliance ? Number(rawValue) : undefined,
				displayName: String(label),
				sonarObjectId: group === SearchOptionGroup.Object ? Number(rawValue) : undefined,
				sonarObjectType: group === SearchOptionGroup.ObjectType ? rawValue as SonarObjectType : undefined,
				tagId: group === SearchOptionGroup.Tag ? Number(rawValue) : undefined,
				viewId: undefined,
				viewName: undefined
			});
		}
	};

	const options = useMemo(() => [
		...typeOptions.map((option) => ({
			...option,
			disabled: !relatedCounts?.[ option.rawValue as SonarObjectType ]
		})),
		...Object.keys(results ?? [])
			.filter((sonarObjectType) => getSortedDataKeys(results).includes(sonarObjectType as SonarObjectType))
			.map((sonarObjectType) => {
				const typeResults = results?.[ sonarObjectType ] ?? [];

				const label =
					<ShowMoreTypeLabel
						allowShowMore={ !objectTypeFilter }
						onClick={ () => setObjectTypeFilter(sonarObjectType) }
						sonarObjectType={ sonarObjectType as SonarObjectType } />;

				return buildOptionsGroup(
					SearchOptionGroup.Object,
					label,
					showMore,
					typeResults,
					(result) => ({
						label: result.displayName,
						value: result.sonarObjectId
					})
				);
			}),
		buildOptionsGroup(
			SearchOptionGroup.View,
			COPY_SPECIFIC.SAVED_VIEWS,
			showMore,
			views,
			(view) => ({
				label: view.name,
				value: view.id
			})
		),
		buildOptionsGroup(
			SearchOptionGroup.Tag,
			COPY_SPECIFIC.TAGS,
			showMore,
			tags,
			(tag) => ({
				label: tag.name,
				value: tag.id
			})
		),
		buildOptionsGroup(
			SearchOptionGroup.Application,
			COPY_SPECIFIC.APPLICATIONS,
			showMore,
			apps,
			(app) => ({
				label: app.name,
				value: app.applicationId
			})
		),
		buildOptionsGroup(
			SearchOptionGroup.Compliance,
			COPY_SPECIFIC.COMPLIANCE,
			showMore,
			complianceCategories,
			([ category, id ]) => ({
				label: category,
				value: id
			})
		)
	], [ apps, relatedCounts, objectTypeFilter, results, showMore, tags, views ]);

	const renderValue: SearchOption | null = useMemo(() => {
		const {
			applicationId,
			complianceCategory,
			displayName,
			sonarObjectId,
			sonarObjectType,
			tagId
		} = queryParams ?? {};

		if (sonarObjectType) {
			return getUniqueOption({
				group: SearchOptionGroup.ObjectType,
				label: <SearchTypeLabel sonarObjectType={ sonarObjectType } />,
				value: sonarObjectType
			});
		} else if (sonarObjectId && displayName) {
			return getUniqueOption({
				group: SearchOptionGroup.Object,
				label: displayName,
				value: sonarObjectId
			});
		} else if (applicationId && displayName) {
			return getUniqueOption({
				group: SearchOptionGroup.Application,
				label: displayName,
				value: applicationId
			});
		} else if (tagId) {
			return getUniqueOption({
				group: SearchOptionGroup.Tag,
				label: <SonarTagDisplay
					noClick
					tagId={ tagId } />,
				value: tagId
			});
		} else if (typeof complianceCategory === 'number') {
			let overrideLabel: ReactNode | undefined = undefined;

			if (complianceCategory === ComplianceCategory.None) {
				overrideLabel = <SonarText>{ COPY_SPECIFIC.NO_COMPLIANCE_CATEGORIES }</SonarText>;
			} else if (complianceCategory === ComplianceCategory.Any) {
				overrideLabel = <SonarText>{ COPY_SPECIFIC.ANY_COMPLIANCE_CATEGORY }</SonarText>;
			}

			return getUniqueOption({
				group: SearchOptionGroup.Compliance,
				label: overrideLabel ?? <ComplianceCategories categoryFlags={ complianceCategory } />,
				value: complianceCategory
			});
		}

		return null;
	}, [ queryParams ]);

	return (
		<Space.Compact
			className='data-dictionary-search'
			size='large'>
			<Select
				allowClear
				className='data-dictionary-select'
				filterOption
				labelInValue
				listHeight={ 720 }
				maxLength={ 128 }
				notFoundContent={ isLoading ? <LoadingSpinner /> : null }
				onClear={ handleOnClear }
				onOpenChange={ handleSearchReset }
				onSearch={ handleOnSearch }
				onSelect={ handleOnSelect }
				optionFilterProp='filterString'
				optionLabelProp='label'
				optionRender={ renderOptions }
				options={ options }
				placeholder={ PLACEHOLDERS_COPY.SEARCH_DATA_DICTIONARY }
				showSearch
				suffixIcon={ null }
				value={ renderValue } />
			<Button
				icon={ <SearchOutlined /> }
				type='primary' />
		</Space.Compact>
	);
};
